import type { FieldValues } from "react-hook-form";
import type { ValidFormField } from "src/components/Form/Form.types.ts";
import { isPlainObject, toFixedWithoutZeros } from "@exonn/utils";
import { normalizeWithCKEditor } from "src/components/Form/utils/normalizeWithCKEditor.ts";
import type { MediaResource } from "@exonn/js-sdk";

export const mapFieldsToFormValues = <
  FormValues extends FieldValues = FieldValues,
>(
  fields: Record<string, ValidFormField<FormValues>>,
  type: "edit" | "create",
  params: Record<string, unknown>,
  searchParams: URLSearchParams,
  data?: any,
): FormValues => {
  return Object.keys(fields).reduce((acc, key) => {
    const field = fields[key];

    const getDefaultValueFromSearchParams = () => {
      if (field.type === "number") {
        return searchParams.get(`initial_${key}`) != null
          ? Number(searchParams.get(`initial_${key}`))
          : null;
      }
      return searchParams.get(`initial_${key}`);
    };

    const getDefaultValue = () => {
      if (field.type === "text") {
        return field.defaultValue ?? "";
      }
      if (field.type === "number") {
        return field.defaultValue !== undefined ? field.defaultValue : 0;
      }
      if (field.type === "checkbox") {
        return field.defaultValue ?? false;
      }
      if (field.type === "switch") {
        return field.defaultValue ?? false;
      }
      if (field.type === "select") {
        return field.defaultValue ?? (field.isMultiple ? [] : "");
      }
      if (field.type === "singleSelect") {
        return field.defaultValue ?? "";
      }
      if (field.type === "multiSelect") {
        return field.defaultValue ?? [];
      }
      if (field.type === "dateRange") {
        return field.defaultValue ?? [null, null];
      }
      if (field.type === "date") {
        return null;
      }
      if (field.type === "image") {
        return [];
      }
      if (field.type === "imageV2") {
        return {};
      }
      if (field.type === "ckeditor") {
        return normalizeWithCKEditor(field.defaultValue ?? "");
      }
      if (field.type === "custom") {
        return field.defaultValue;
      }
      if (field.type === "radio") {
        return field.defaultValue;
      }
      if (field.type === "segmentedControl") {
        return field.defaultValue;
      }
      if (field.type === "textArea") {
        return "";
      }

      return "";
    };

    const getValue = () => {
      const value = data?.[key];

      if (field.valueGetter && data != null && type === "edit") {
        return field.valueGetter(value, data);
      }

      if (field.type === "imageV2") {
        if (Array.isArray(value)) {
          return value.reduce((acc, el: MediaResource) => {
            return {
              ...acc,
              [el.uuid]: el,
            };
          }, {});
        }
      }

      if (field.type === "multiSelect" && field?.serverOptions != null) {
        if (
          value &&
          Array.isArray(value) &&
          value.length > 0 &&
          isPlainObject(value[0]) &&
          Object.prototype.hasOwnProperty.call(value[0], "id")
        ) {
          return value.map((resource) => resource.id);
        }
      }

      if (field.type === "ckeditor") {
        return normalizeWithCKEditor(value);
      }

      if (field.type === "number") {
        if (field.emptyWhenZero && +value === 0) {
          return null;
        }

        // cutting off trailing zeros
        if (typeof value === "string") {
          return +toFixedWithoutZeros(+value, 6);
        }
        if (typeof value === "number") {
          return +toFixedWithoutZeros(value, 6);
        }
      }

      return value;
    };

    const value =
      getValue() ?? getDefaultValueFromSearchParams() ?? getDefaultValue();

    return {
      [key]: value,
      ...acc,
    };
  }, {} as FormValues);
};
